import {Box, Divider, Typography} from '@mui/material';
import React, {Fragment, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';

import {TypeLocalNameEnum} from 'domain/usecases';
import {AppointmentBookingEnum, FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {
	CardList,
	DataCard,
	DataInfo,
	SelectLocationContainer,
	StyledGrid,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {OdontoBookingEnum, QueryParamsEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {
	BookingEmptyState,
	BookingErrorState,
	BookingNotFoundDialog,
	BookingNotFoundState,
} from 'presentation/pages/private/booking/components';
import {BookingNotFoundVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundDialog/types';
import {BookingNotFoundStateVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundState/types';
import {BookingFlowType, FlowEnum, PATH_TITLE} from 'presentation/pages/private/booking/constants';

import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {
	useAccountInformation,
	useMediaQueries,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {LocationType} from 'presentation/pages/private/store/locationStore/types';
import {translations} from 'presentation/translations-new';
import {
	formatText,
	CHANGE_PLAN_LOCATION_FLOW,
	FlowEnumWithoutProfile,
	FlowTypeEnum,
	getInitialPath,
	getPageHeaderTitle,
	getUfPath,
	getUserInfoFromToken,
	usernameWithoutSpaces,
} from 'presentation/utils';
import {textHandle} from 'presentation/utils/textHandle';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useGetCitiesQuery} from '../../hooks/useGetCitiesQuery';
import {CityListProps} from './types';
import {useNotFound} from 'presentation/pages/private/hooks/useNotFound';
import {getBookingTypeAndOrigin} from 'presentation/modules/booking/shared/utils/getBookingTypeAndOrigin';
import {NotFoundVariantEnum} from 'presentation/modules/guide/shared/ui/components/DistrictSelection/components/NotFoundDialog/types';
import {NotFoundDialog} from 'presentation/modules/guide/shared/ui/components/DistrictSelection/components/NotFoundDialog';

const {city, emptySearchState} = translations['pt-br'].privateFlow.profile.locationSelection;
const {booking} = translations['pt-br'].others.sidebar;
const {change, selectLocality} = translations['pt-br'].commons;
const {appointmentState} = translations['pt-br'].privateFlow.profile;
const {notFoundCity} = translations['pt-br'].privateFlow.requestAuthorizations.emptySearch;

export function CityList({flowType}: CityListProps) {
	const navigate = useNavigate();
	const {handleCallChatRoom, handleGuideNav, notFoundModalIsOpen, setNotFoundModalIsOpen} =
		useNotFound();
	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const {beneficiary} = useAccountInformation();
	const {isMobile} = useMediaQueries();
	const getLocationTypeFromFlow = (): LocationType => {
		if (isAuthorizationFlow) return FlowType.AUTHORIZATION;
		if (isOdontoFlow) return FlowType.ODONTO;
		if (flowType === FlowEnum.HEALTH_GUIDE) return FlowType.HEALTH_GUIDE;
		if (flowType === FlowEnum.ODONTO_GUIDE) return FlowType.ODONTO_GUIDE;
		return FlowType.DEFAULT;
	};

	const setLocation = useLocationStore(state => state.setLocation);

	const {SEARCH_PARAMS, clearSearch} = useQueryParams();
	const {ufCode, cityName, ufName, query} = SEARCH_PARAMS;

	const isAuthorizationFlow = flowType === FlowEnum.AUTHORIZATIONS;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isChangePlanFlow = flowType === CHANGE_PLAN_LOCATION_FLOW;
	const flowTypeForBookingComponents: FlowEnumWithoutProfile = isChangePlanFlow
		? FlowEnum.HEALTH
		: flowType;

	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});
	const initialPath = getInitialPath(flowType);
	const bookingUfPath = getUfPath(flowType);
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isGuideFlow = flowType === FlowEnum.HEALTH_GUIDE || flowType === FlowEnum.ODONTO_GUIDE;
	const flowTypeForBookingDialog: FlowTypeEnum =
		isChangePlanFlow || isGuideFlow ? FlowEnum.HEALTH : flowType;
	const {data: healthCard, isLoading: isHealthCardLoading} = useGetHealthCardQuery();
	const cdPlanoANSOdonto =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns;
	const nuRegistroPlano = beneficiary?.nuRegistroPlano;
	const {currentEmail, currentPhone} = usePersonalData();
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);

	const {SPECIALITY_OR_EXAM_STEP} = getStepPosition({
		flowType: flowTypeForBookingComponents,
		isManualLocation: bookingData?.isManualLocation,
	});

	const {
		data: cityList,
		isLoading: loading,
		isError: error,
		refetch: refresh,
	} = useGetCitiesQuery({
		cdUf: ufCode,
		flowType,
		cdPlanoANSOdonto,
		nuRegistroPlano,
	});

	const handleBack = useCallback(() => {
		navigate(
			`${bookingUfPath}?${QueryParamsEnum.UF_CODE}=${ufCode}&${QueryParamsEnum.UF_NAME}=${ufName}`,
		);
	}, [bookingUfPath, ufCode, ufName]);

	const handleConfirmSelection = (nmCity: string) => {
		const locationType = getLocationTypeFromFlow();
		setLocation(cdPessoaC, locationType, {
			state: {
				cdUf: ufCode,
				nmUf: ufName,
			},
			city: nmCity,
			typeLocalName: TypeLocalNameEnum.CIDADE,
		});
		clearSearch();

		if (isOdontoFlow) {
			const {bookingType, bookingOrigin} = getBookingTypeAndOrigin(
				flowType as BookingFlowType,
			);

			setBookingData(prev => ({
				...prev,
				email: currentEmail,
				phone: currentPhone,
				ufCode,
				ufName,
				cityName: nmCity,
				typeLocalName: TypeLocalNameEnum.CIDADE,
				bookingType,
				bookingOrigin,
			}));

			setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);

			navigate(OdontoBookingEnum.ODONTO_BOOKING_FLOW, {
				replace: true,
			});
			return;
		}

		if (isChangePlanFlow) {
			navigate(
				`${initialPath}?${QueryParamsEnum.UF_CODE}=${encodeURIComponent(ufCode)}&${QueryParamsEnum.UF_NAME}=${encodeURIComponent(ufName)}&${QueryParamsEnum.CITY_NAME}=${encodeURIComponent(nmCity)}`,
				{replace: true},
			);
			return;
		}

		navigate(initialPath);
	};

	const getEmptyDescription = () => {
		if (isGuideFlow) {
			return emptySearchState.notFoundAllGuideFlow;
		}

		if (isAuthorizationFlow) {
			return notFoundCity;
		}
		if (isOdonto) {
			return emptySearchState.odontonotFoundAllCity;
		}

		return emptySearchState.notFoundAllCity;
	};

	if (loading || isHealthCardLoading) {
		return <Loading minHeight="50%" text={city.loadingCities} />;
	}

	if (error) {
		return (
			<BookingErrorState
				pageTitle={pageTitle}
				errorTitle={city.loadingError}
				handleBack={handleBack}
				handleRefresh={refresh}
				showHeader={false}
			/>
		);
	}

	const NOT_FOUND = !cityList || cityList?.length === 0;

	if (NOT_FOUND) {
		return (
			<Box p={isMobile ? 2 : 4} mt={2}>
				<BookingNotFoundState
					flowType={flowTypeForBookingComponents}
					pageTitle={pageTitle}
					handleBack={handleBack}
					variant={BookingNotFoundStateVariantEnum.CITY}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_CIDADE_DISPONIVEL}
					showHeader={false}
				/>
			</Box>
		);
	}

	const sanitizedQuery = textHandle(query).sanitizeText();
	const FILTERED_CITY_LIST = cityList?.filter(cityItem => {
		const sanitizedCity = textHandle(
			cityItem?.nmCidade ? cityItem.nmCidade : '',
		).sanitizeText();
		return sanitizedCity.includes(sanitizedQuery);
	});

	const isListEmpty = FILTERED_CITY_LIST?.length === 0;

	return (
		<Fragment>
			{isListEmpty ? (
				<BookingEmptyState
					description={getEmptyDescription()}
					internalPadding
					flowType={flowTypeForBookingComponents}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_CIDADE_ENCONTRADA}
				/>
			) : (
				<SelectLocationContainer
					customOrientationText={city.orientationCity}
					buttonDataTagId={AppointmentBookingEnum.CLICK_NAO_ENCONTREI_CIDADE_DESEJADA}
					handleOpenModal={() => setNotFoundModalIsOpen(true)}
					flowType={flowTypeForBookingComponents}>
					<Fragment>
						{isOdontoFlow ? (
							<Typography variant="subtitle2" color="text.secondary">
								{selectLocality}
							</Typography>
						) : (
							<Fragment>
								<DataCard
									handleAction={handleBack}
									subtitle={appointmentState}
									buttonText={change}
									footerComponent={
										<DataInfo
											primaryText={formatText(ufName)}
											iconName="my-location-filled"
										/>
									}
								/>
								<Divider />
							</Fragment>
						)}

						<StyledGrid.Root>
							{FILTERED_CITY_LIST?.map((item, index) => (
								<StyledGrid.Item
									sm={12}
									md={12}
									lg={FILTERED_CITY_LIST?.length > 1 ? 6 : 12}
									key={`${item?.nmCidade}-${index}`}>
									<CardList
										dataTagId={`${AppointmentBookingEnum.SELECIONOU_CIDADE}${usernameWithoutSpaces(item?.nmCidade)}`}
										handleClick={() => handleConfirmSelection(item?.nmCidade)}
										showArrowRightIcon={isOdontoFlow}
										text={formatText(item.nmCidade)}
										selected={
											cityName?.toLowerCase() ===
											item?.nmCidade?.toLowerCase()
										}
									/>
								</StyledGrid.Item>
							))}
						</StyledGrid.Root>
					</Fragment>
				</SelectLocationContainer>
			)}
			{isGuideFlow ? (
				<NotFoundDialog
					variant={NotFoundVariantEnum.CITY}
					handleCallChatRoom={() => handleCallChatRoom(flowType)}
					handleGuideNav={() => handleGuideNav()}
					open={notFoundModalIsOpen}
					setOpen={setNotFoundModalIsOpen}
				/>
			) : (
				<BookingNotFoundDialog
					flowType={flowTypeForBookingDialog}
					variant={BookingNotFoundVariantEnum.CITY}
					handleCallChatRoom={() => handleCallChatRoom(flowTypeForBookingDialog)}
					handleGuideNav={() => handleGuideNav(flowTypeForBookingDialog)}
					open={notFoundModalIsOpen}
					setOpen={setNotFoundModalIsOpen}
				/>
			)}
		</Fragment>
	);
}
